/**
 * TODO: reorganize this file — one entity per file, or logical grouping of related entities
 * where it makes sense. This grew into a catch-all and now also affects bundle size since
 * everything importing a single helper pulls in the whole module. Deferred until after the
 * wahts branch merges to dev to avoid piling up more conflicts in the meantime.
 */
/* eslint-disable consistent-return, max-lines */
import type {
  Configuration,
  Country,
  EntryPoint,
  Navigation,
  NavigationItem,
  Product,
  SiteType,
  Subscription,
} from '../graphql/codegen';
import type { Hit, SearchResponse } from '@algolia/client-search';
import type { Maybe } from 'graphql/jsutils/Maybe';
import type { RuleSet } from 'styled-components';

import { css } from 'styled-components';

import fetchService from './fetchService';
import { tiersObj } from '../../config/localization.js';
import { CustomEventTypes } from '../constants';
import {
  RECENTLY_VIEWED_PRODUCTS_TIMESTAMP,
  RVP_KEY,
  RVP_LIST_LENGTH_LIMIT,
  SIZE_TRANSFER_LENGTH,
  SIZE_TRANSFER_RANGE_IS_WAIST_LENGTH,
  SIZE_TRANSFER_SIZE,
} from '../constants/localStorageKeys';
import logger from '../logger';
import { buildApiUrl } from '../parsers';
import type { SearchResultProductColor } from '~/components/shared/header/searchBoxV2/types';
import type { AlgoliaProductHit, LocaleStringObject } from '~/types/algolia';

// https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API#feature-detecting_localstorage
export const storageAvailable = (type: 'localStorage' | 'sessionStorage') => {
  let storage: Storage;

  try {
    storage = window[type];

    const x = '__storage_test__';

    storage.setItem(x, x);
    storage.removeItem(x);

    return true;
  } catch (e) {
    return (
      e instanceof DOMException &&
      // error codes for all browsers except Firefox
      (e.code === 22 ||
        // Firefox
        e.code === 1014 ||
        // test name field too, because code might not be present
        // everything except Firefox
        e.name === 'QuotaExceededError' ||
        // Firefox
        e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
      // acknowledge QuotaExceededError only if there's something already stored
      window[type] &&
      window[type].length !== 0
    );
  }
};

export type RVPImage = {
  altText?: string;
  base64BackgroundImage?: string;
  imageType?: string;
  url?: string;
  isAI?: string;
};

export type RecentlyViewedProduct = Product & {
  sku?: string;
  primaryImage?: RVPImage;
  hoverImage?: RVPImage;
  formattedBasePrice?: string;
  formattedFromPrice?: string;
  color?: string | SearchResultProductColor;
  stockQuantity?: number;
  basePrice?: number;
};

export const getRecentlyViewedProductsFromLocalStorage = (
  country: string
): RecentlyViewedProduct[] => {
  try {
    if (storageAvailable('localStorage')) {
      const rvpKey = RVP_KEY(country);
      const rvp = JSON.parse(localStorage.getItem(rvpKey) || '[]');

      if (Array.isArray(rvp)) {
        const result: RecentlyViewedProduct[] = rvp
          .map(p => (!p.code && !p.baseProduct ? { ...p, code: p.sku } : p))
          .map(p => (!p.sku ? { ...p, sku: p.code || p.baseProduct } : p))
          .filter(p => p.sku);

        return result;
      }
    }
  } catch (err) {
    logger.error(err as Error);
  }

  return [];
};

export const removeExpiredProducts = async (
  localStorageProducts: RecentlyViewedProduct[],
  csrfToken: string,
  locale: string,
  country: string
) => {
  const SERVER_SYNC_EXPIRY = (Date.now() - 24 * 60 * 60 * 1000).toString();
  const timestamp = localStorage.getItem(RECENTLY_VIEWED_PRODUCTS_TIMESTAMP) || '0';
  const isExpired = !timestamp || timestamp < SERVER_SYNC_EXPIRY;

  let filteredRvpProducts;

  if (!csrfToken || !isExpired) {
    return localStorageProducts;
  }

  try {
    const localSkuList = localStorageProducts.map(product => product.sku?.toUpperCase());
    const url = buildApiUrl('/recentlyViewedProducts/filter', locale);

    const response = await fetchService.post(
      url,
      { skuList: localSkuList },
      {
        headers: {
          'Content-Type': 'application/json',
          CSRFToken: csrfToken,
        },
        cache: 'no-cache',
      }
    );

    const {
      data: { skuList },
    } = response as { data: { skuList?: string[] } };

    if (!skuList) {
      return localStorageProducts;
    }

    localStorage.setItem(RECENTLY_VIEWED_PRODUCTS_TIMESTAMP, JSON.stringify(Date.now()));

    if (!skuList.length) {
      return localStorageProducts;
    }

    filteredRvpProducts = localStorageProducts.filter(product => {
      const priceValue = product.price?.value?.toString() || product?.basePrice?.toString();

      return !skuList.includes(product.sku || '') && /[1-9]+/gim.test(priceValue || '');
    });

    localStorage.setItem(RVP_KEY(country), JSON.stringify(filteredRvpProducts));

    window.dispatchEvent(new CustomEvent(CustomEventTypes.RECENTLY_VIEWED_PRODUCTS_LIST_UPDATED));

    return filteredRvpProducts;
  } catch (error) {
    return localStorageProducts;
  }
};

export const addProductToRecentlyViewedLocalStorage = (country: string, product: Product) => {
  try {
    const isLocalStorageAvailable = storageAvailable('localStorage');

    if (!isLocalStorageAvailable) {
      return;
    }

    const localStorageProducts = getRecentlyViewedProductsFromLocalStorage(country);
    const currentIndex = localStorageProducts.findIndex(
      (p: RecentlyViewedProduct) =>
        p.sku?.toLowerCase() === product.code?.toLowerCase() ||
        p.sku?.toLowerCase() === product.baseProduct?.toLowerCase()
    );

    if (currentIndex >= 0) {
      localStorageProducts.splice(currentIndex, 1);
    }

    localStorageProducts.unshift({
      ...(product as RecentlyViewedProduct),
      sku: product.baseProduct || product.code,
    });

    if (isLocalStorageAvailable && localStorageProducts.length) {
      if (localStorageProducts.length > RVP_LIST_LENGTH_LIMIT) {
        localStorageProducts.splice(
          RVP_LIST_LENGTH_LIMIT,
          localStorageProducts.length - RVP_LIST_LENGTH_LIMIT
        );
      }

      localStorage.setItem(RVP_KEY(country), JSON.stringify(localStorageProducts));

      const customEvent = new CustomEvent(CustomEventTypes.RECENTLY_VIEWED_PRODUCTS_LIST_UPDATED);

      window.dispatchEvent(customEvent);
    }
  } catch (error) {
    console.error(error);
  }
};

export const getLocation = () =>
  window.location.pathname + window.location.hash.replace('#', '?__');

export const checkEnvTiersAndLocales = (locale: string): boolean => {
  let toFetch = false;
  const envLocales = process.env.LOCALE?.trim()
    .toLowerCase()
    ?.split(',')
    .filter(l => !!l);
  const envTiers = process.env.TIER?.trim()
    .toLowerCase()
    ?.split(',')
    .filter(t => !!t);

  if (!envLocales?.length && !envTiers?.length) {
    toFetch = true;
  } else {
    toFetch = !!envLocales?.includes(locale);

    const tiers = tiersObj as { [k: string]: string[] };

    envTiers?.forEach(tier => {
      if (tiers[tier].includes(locale)) {
        toFetch = true;
      }
    });
  }

  return toFetch;
};

export const getLocalesToBeBuiltWithEnvTiersLocales = (locales: string[]) => {
  let localesToBeBuilt: string[] = [];

  const envLocales = process.env.LOCALE?.trim()
    .toLowerCase()
    ?.split(',')
    .filter(l => !!l);

  if (envLocales && envLocales.length) {
    localesToBeBuilt = envLocales.filter(locale => {
      if (locales.includes(locale)) {
        return true;
      }

      logger.warn(`Locale ${locale} does not exist.`);

      return false;
    });
  }

  const envTiers = process.env.TIER?.trim()
    .toLowerCase()
    ?.split(',')
    .filter(t => !!t);
  const tiers = tiersObj as { [k: string]: string[] };

  if (envTiers && envTiers.length) {
    envTiers.forEach(tier => {
      if (tiers[tier]) {
        localesToBeBuilt = [
          ...localesToBeBuilt,
          ...tiers[tier].filter(tierLocale => locales?.includes(tierLocale)),
        ];
      } else {
        logger.warn(`Tier ${tier} does not exist.`);
      }
    });
  }

  localesToBeBuilt = localesToBeBuilt.filter(
    (locale, index) => localesToBeBuilt.indexOf(locale) === index
  );

  if (!localesToBeBuilt.length) {
    localesToBeBuilt = locales;
  }

  return localesToBeBuilt;
};

type ActiveNodeType =
  | { navItem: NavigationItem | null | undefined; indexArray: string[] }
  | undefined;

const getActiveNode = (n: NavigationItem | undefined | null, plpPath: string): ActiveNodeType => {
  if (!n?.children && n?.url && n.url === plpPath) {
    return { navItem: n, indexArray: [] };
  }

  for (let i = 0; i < (n?.children ?? []).length; i++) {
    const node = getActiveNode((n?.children ?? [])[i], plpPath);

    if (node) {
      node.indexArray.unshift(`${i}`);

      return node;
    }
  }
};

export const getActiveNavItems = (nav: Maybe<Navigation>, plpPath: string): ActiveNodeType => {
  if (!nav) {
    return;
  }

  for (let i = 0; i < (nav.navigation ?? []).length; i++) {
    const navItem = (nav.navigation ?? [])[i];
    const node = getActiveNode(navItem, plpPath);

    if (node) {
      node.indexArray.unshift(`${i}`);

      return node;
    }
  }
};

export const getStyleObject = (styleModifier: string) => {
  const styleRegex = /style="([^"]*)"/;
  const matches = styleRegex.exec(styleModifier);
  const cssStyleString = matches && matches.length > 0 ? matches[1] : undefined;

  if (!cssStyleString) {
    return undefined;
  }

  const styleObject: {
    [key: string]: string;
  } = {};

  cssStyleString.split(';').forEach(pair => {
    const [key, value] = pair.split(':');

    if (key && value) {
      const camelCaseKey = key.trim().replace(/-(\w)/g, (_, c) => c.toUpperCase());

      styleObject[camelCaseKey] = value.trim();
    }
  });

  return styleObject;
};

export const getClassNames = (styleModifier: string) => {
  const classNameRegex = /class="([^"]*)"/;
  const matches = classNameRegex.exec(styleModifier);
  const classNames = matches && matches.length > 0 ? matches[1] : undefined;

  return classNames;
};

const T_SHIRT_SIZES_ORDER: Record<string, number> = {
  '3xs': 1,
  xxxs: 1,
  '2xs': 2,
  xxs: 2,
  xs: 3,
  s: 4,
  m: 5,
  l: 6,
  xl: 7,
  '1x': 7,
  '1xl': 7,
  xxl: 8,
  '2x': 8,
  '2xl': 8,
  xxxl: 9,
  '3x': 9,
  '3xl': 9,
  os: 99,
  'o/s': 99,
  onesize: 99,
  'one size': 99,
  '1size': 99,
  tu: 99,
};

export const getLetterSizeRank = (size: string): number | undefined => {
  const normalized = size.trim().toLowerCase();

  if (T_SHIRT_SIZES_ORDER[normalized] !== undefined) {
    return T_SHIRT_SIZES_ORDER[normalized];
  }

  const noSpaces = normalized.replace(/[\s_]+/g, '');

  if (T_SHIRT_SIZES_ORDER[noSpaces] !== undefined) {
    return T_SHIRT_SIZES_ORDER[noSpaces];
  }

  // Handle ranges like "XL-XXL", "XS/S", "M-L", "S - M"
  const parts = normalized.split(/[-/]/).map(p => p.trim());

  if (parts.length === 2) {
    const rank1 = getLetterSizeRank(parts[0]);
    const rank2 = getLetterSizeRank(parts[1]);

    if (rank1 !== undefined && rank2 !== undefined) {
      return rank1 + (rank2 - rank1) * 0.5;
    }
  }

  return undefined;
};

const extractNumbers = (size: string): number[] => {
  const matches = size.match(/\d+/g);

  if (!matches) {
    return [];
  }

  return matches
    .map(match => {
      try {
        return Number.parseInt(match, 10);
      } catch {
        return Number.NaN;
      }
    })
    .filter(n => !Number.isNaN(n));
};

export const sortSizeFacetsCallback = (prevItem: { name: string }, nextItem: { name: string }) => {
  if (prevItem.name === nextItem.name) {
    return 0;
  }

  const rankA = getLetterSizeRank(prevItem.name);
  const rankB = getLetterSizeRank(nextItem.name);

  if (rankA !== undefined && rankB !== undefined) {
    if (rankA !== rankB) {
      return rankA < rankB ? -1 : 1;
    }

    return prevItem.name.localeCompare(nextItem.name, undefined, { numeric: true });
  }

  const numbersA = rankA === undefined ? extractNumbers(prevItem.name) : [];
  const numbersB = rankB === undefined ? extractNumbers(nextItem.name) : [];
  const hasNumbersA = numbersA.length > 0;
  const hasNumbersB = numbersB.length > 0;

  if (hasNumbersA && hasNumbersB) {
    if (numbersA[0] !== numbersB[0]) {
      return numbersA[0] < numbersB[0] ? -1 : 1;
    }

    if (numbersA.length > 1 && numbersB.length > 1 && numbersA[1] !== numbersB[1]) {
      return numbersA[1] < numbersB[1] ? -1 : 1;
    }

    if (numbersA.length !== numbersB.length) {
      return numbersA.length < numbersB.length ? -1 : 1;
    }

    return prevItem.name.localeCompare(nextItem.name, undefined, { numeric: true });
  }

  if (hasNumbersA && !hasNumbersB) {
    return -1;
  }

  if (!hasNumbersA && hasNumbersB) {
    return 1;
  }

  if (rankA !== undefined && rankB === undefined) {
    return -1;
  }

  if (rankA === undefined && rankB !== undefined) {
    return 1;
  }

  return prevItem.name.localeCompare(nextItem.name, undefined, { numeric: true });
};

export const syncStorageSizeFilter = ({ size, length }: { size?: string[]; length?: string[] }) => {
  if (storageAvailable('localStorage')) {
    if (size) {
      localStorage.setItem(SIZE_TRANSFER_SIZE, JSON.stringify(size));
    } else {
      localStorage.removeItem(SIZE_TRANSFER_SIZE);
    }

    if (length) {
      localStorage.setItem(SIZE_TRANSFER_LENGTH, JSON.stringify(length));
    } else {
      localStorage.removeItem(SIZE_TRANSFER_LENGTH);
    }
  }
};

export const syncStorageWaistFlag = ({
  hasSize,
  hasLength,
}: {
  hasSize: boolean;
  hasLength: boolean;
}) => {
  /**
   * Functionality copied from current-stack code:
   * https://bitbucket.org/gstar-ondemand/hybris/src/master/hybris/bin/custom/gstar/gstarstorefront/web/webroot/gstar/js/app/components/productLister--v2/SizeSelectionTransfer.js#lines-64:70
   */
  if (storageAvailable('localStorage')) {
    localStorage.setItem(SIZE_TRANSFER_RANGE_IS_WAIST_LENGTH, JSON.stringify(hasSize && hasLength));
  }
};

export const formatNumberByMask = (number: number, mask: string): string => {
  const invalidDecimalMaskErrorMessage = `Invalid mask: "${mask}". Cannot have multiple decimal separators.`;
  const invalidThousandsMaskErrorMessage = `Invalid mask: "${mask}". Cannot have multiple thousands group separators.`;
  const numberErrorMessage = `The provided value "${number}" is not a number.`;

  if (typeof number !== 'number' || Number.isNaN(number)) {
    throw new Error(numberErrorMessage);
  }

  let decimalSeparator = '';

  if (mask.includes('.0')) {
    if (decimalSeparator !== '') {
      throw new Error(invalidDecimalMaskErrorMessage);
    }

    decimalSeparator = '.';
  }

  if (mask.includes(',0')) {
    if (decimalSeparator !== '') {
      throw new Error(invalidDecimalMaskErrorMessage);
    }

    decimalSeparator = ',';
  }

  if (mask.includes("'0")) {
    if (decimalSeparator !== '') {
      throw new Error(invalidDecimalMaskErrorMessage);
    }

    decimalSeparator = "'";
  }

  const [integerPart, decimalPart] =
    decimalSeparator !== '' ? mask.split(decimalSeparator) : [mask, ''];
  const decimalPlaces = decimalPart ? decimalPart.length : 0;

  let thousandSeparator = '';

  if (integerPart.includes(',')) {
    if (thousandSeparator !== '') {
      throw new Error(invalidThousandsMaskErrorMessage);
    }

    thousandSeparator = ',';
  }

  if (integerPart.includes(' ')) {
    if (thousandSeparator !== '') {
      throw new Error(invalidThousandsMaskErrorMessage);
    }

    thousandSeparator = ' ';
  }

  if (integerPart.includes(' ')) {
    if (thousandSeparator !== '') {
      throw new Error(invalidThousandsMaskErrorMessage);
    }

    thousandSeparator = ' ';
  }

  if (integerPart.includes('.')) {
    if (thousandSeparator !== '') {
      throw new Error(invalidThousandsMaskErrorMessage);
    }

    thousandSeparator = '.';
  }

  if (integerPart.includes("'")) {
    if (thousandSeparator !== '') {
      throw new Error(invalidThousandsMaskErrorMessage);
    }

    thousandSeparator = "'";
  }

  if (integerPart.includes('’')) {
    if (thousandSeparator !== '') {
      throw new Error(invalidThousandsMaskErrorMessage);
    }

    thousandSeparator = '’';
  }

  const groupSize = Math.max(...integerPart.split(thousandSeparator).map(part => part.length));

  let formattedNumber = number.toFixed(decimalPlaces);
  // eslint-disable-next-line prefer-const
  let [integerDigits = '', decimalDigits = ''] = formattedNumber.split('.');

  if (thousandSeparator) {
    let groupedInteger = '';
    let integerLength = integerDigits.length;

    while (integerLength > 0) {
      if (groupedInteger) {
        groupedInteger = thousandSeparator + groupedInteger;
      }

      const substringStart = Math.max(0, integerLength - groupSize);

      groupedInteger = integerDigits.substring(substringStart, integerLength) + groupedInteger;
      integerLength -= groupSize;
    }

    integerDigits = groupedInteger;
  }

  if (decimalPlaces > 0) {
    formattedNumber = `${integerDigits}${decimalSeparator}${decimalDigits}`;
  } else {
    formattedNumber = integerDigits;
  }

  return formattedNumber;
};

type SubscribeRequest = {
  isNewsLetterSubscription: boolean;
  email: string;
  entryPoint: EntryPoint;
  productCode?: string;
  gridValue1?: string;
  gridValue2?: string;
  password?: string;
  name?: string;
  lastName?: string;
  preferredCategory?: string;
};

export const subscribeToGstar = async (params: SubscribeRequest): Promise<Subscription> => {
  const {
    isNewsLetterSubscription,
    entryPoint,
    productCode,
    gridValue1,
    gridValue2,
    preferredCategory,
    email,
    password,
    name,
    lastName,
  } = params;
  const query = new URLSearchParams();

  query.set('isNewsLetterSubscription', isNewsLetterSubscription ? 'true' : 'false');
  query.set('entryPoint', entryPoint);

  if (productCode) {
    query.set('productCode', productCode);
  }

  if (gridValue1) {
    query.set('gridValue1', gridValue1);
  }

  if (gridValue2) {
    query.set('gridValue2', gridValue2);
  }

  if (preferredCategory) {
    query.set('preferredCategory', preferredCategory);
  }

  const formData = new FormData();

  formData.append('email', email);
  formData.append('password', password || '');
  formData.append('name', name || '');
  formData.append('lastName', lastName || '');

  const url = buildApiUrl(`api/v3/user/subscribe?${query.toString()}`);
  const response = (await fetchService.postFormData<Subscription>(url, formData)).data;

  return response;
};

export const escapePercentageDiscountValue = (value: string): string => {
  const escapedValue = value.replace(/^-/, '\\-');

  return escapedValue;
};

export const checkForFacetKeysInPath = (facetKeys: string[], path: string): boolean =>
  facetKeys?.some(item => {
    const re = new RegExp(`[&?]${item}=`);

    return re.test(path);
  });

export const extractLocalesFromCountries = (countries: Array<Country>): string[] =>
  countries
    ?.map((country): string[] => {
      const languages: string[] = [];

      country?.supportedLanguages?.forEach(language => {
        languages.push(String(language?.isocode).toLowerCase());
      });

      return languages;
    })
    ?.flat();

export function getGraphqlServerUriForClientSide(
  window: Window,
  configuredClientSideEndpoint: string = 'http://localhost:5000/graphql'
) {
  if (configuredClientSideEndpoint?.startsWith('http')) {
    return configuredClientSideEndpoint;
  }

  if (/(tst|dev|acc|prd)\d+-storefront/.test(window?.location.hostname)) {
    return `${window.location.origin.replace(
      'storefront',
      'graphql'
    )}${configuredClientSideEndpoint}`;
  }

  return `${window?.location.origin || ''}${configuredClientSideEndpoint}`;
}

export const localisedData = (
  hit: AlgoliaProductHit | Hit<SearchResponse>,
  localisedData: LocaleStringObject,
  language: string
): string => {
  if (localisedData !== null && typeof localisedData === 'object') {
    if (language in localisedData) {
      return localisedData[language];
    }

    const message = `Malformed localized algolia object for product "${
      (hit as AlgoliaProductHit).code
    }".\n Expected locales: "${language}" and "EN", but got only "${Object.keys(localisedData)}"\n`;

    if ('EN' in localisedData) {
      logger.debug(message);
    } else {
      logger.warn(message);
    }

    return localisedData.EN;
  }

  return localisedData;
};

export const getLocalizedData = (
  language: string,
  value?: string | null
): LocaleStringObject | undefined => {
  if (!value) {
    return undefined;
  }

  if (language === 'EN') {
    return { EN: value };
  }

  return { [language]: value, EN: value };
};

type SiteStyleMap = Partial<Record<SiteType, RuleSet<object>>> & {
  /** Fallback applied when no site-specific entry matches. */
  default?: RuleSet<object>;
};

type SiteStyleGroup = {
  /** A single site, or several sites sharing the same styles. */
  sites: SiteType | SiteType[];
  styles: RuleSet<object>;
};

export const getSiteStyles = (
  siteType: SiteType,
  siteStyles: SiteStyleMap | SiteStyleGroup[],
  defaultStyles: RuleSet<object> = css``
): RuleSet<object> => {
  if (Array.isArray(siteStyles)) {
    const group = siteStyles.find(({ sites }) =>
      Array.isArray(sites) ? sites.includes(siteType) : sites === siteType
    );

    return group?.styles ?? defaultStyles;
  }

  return siteStyles[siteType] ?? siteStyles.default ?? defaultStyles;
};

export function* chunks<T>(arr: T[], n: number): Generator<T[], void> {
  for (let i = 0; i < arr.length; i += n) {
    yield arr.slice(i, i + n);
  }
}

export const getPpeDeliveryKeys = (configuration: Configuration): { key: string }[] =>
  (configuration?.personalisedDiscountTrafficSources ?? []).map(key => ({
    key: (key ?? '').toLowerCase().replace(/_/g, '-'),
  }));
